<template>

    <rkm-modal
        :loading="loading"
        large
        ref="RkmModal"
        :title="editando ? 'Editar núcleo familiar' : 'Criar núcleo familiar'"
    >

        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row class="flex-nowrap">
                <rkm-col>
                    <rkm-form-input
                        label="Identificação do núcleo familiar"
                        placeholder="Ex.: nome do responsável familiar ou referência do domicílio (Família Silva)"
                        v-model="nome"
                        uppercase
                        :validations="validations.nome"
                    />
                </rkm-col>
            </rkm-row>

            <template v-if="editando">

                <rkm-row class="flex-nowrap" v-if="residentesForaDoNucleo.length > 0">
                    <rkm-col>
                        <rkm-form-select
                            :options="residentesForaDoNucleo"
                            label="Adicionar membro"
                            show-default-option
                            text-field="nome"
                            v-model="membroParaAdicionarId"
                            default-option-text="Selecione"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            color="primary"
                            :disabled="!membroParaAdicionarId"
                            @click="handleAddMembro"
                        >
                            {{ membroSelecionadoOutroNucleo ? 'Alterar' : 'Incluir' }}
                        </rkm-button>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-table
                            :fields="fields"
                            :items="nucleo.membros"
                            sort-by="nome"
                        >
                            <template v-slot:cell(nome)="{ value, item }">
                                {{ value }}
                                <rkm-icon
                                    v-if="residenteInfo(item.id).isResponsavel"
                                    icon="crown"
                                    title="Responsável familiar"
                                />
                            </template>

                            <template v-slot:cell(responsavelFamiliar)="{ item }">
                                {{ residenteInfo(item.id).responsavelFamiliar || '-' }}
                            </template>

                            <template v-slot:cell(parentesco)="{ item }">
                                {{ residenteInfo(item.id).parentesco || '-' }}
                            </template>

                            <template v-slot:cell(actions)="{ item }">
                                <rkm-button
                                    @click.stop="removeMembro(item)"
                                    color="danger"
                                    title="Remover do núcleo"
                                    small
                                >
                                    <rkm-icon icon="trash" />
                                </rkm-button>
                            </template>
                        </rkm-table>
                    </rkm-col>
                </rkm-row>
            </template>
        </rkm-form>

        <template v-slot:footer>
            <rkm-button @click="handleCancel()">
                Cancelar
            </rkm-button>

            <rkm-button
                @click="handleSalvar()"
                color="primary"
            >
                Salvar
            </rkm-button>
        </template>

    </rkm-modal>
</template>

<script>

import { repository, error, stringCase } from '@/mixins'

export default {
    mixins: [ repository, error, stringCase ],
    props: {
        data: { type: Object, required: true },
    },
    data: () => ({
        loading: false,
        editando: false,
        nucleo: null,
        nome: '',
        membroParaAdicionarId: null,
        membrosOriginaisIds: [],
        fields: [
            {
                key: 'nome',
                label: 'Nome',
                sortable: true
            },
            {
                key: 'responsavelFamiliar',
                label: 'Responsável familiar',
                sortable: true
            },
            {
                key: 'parentesco',
                label: 'Grau de parentesco',
                sortable: true
            },
            {
                name: 'actions',
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
    }),
    computed: {
        validations() {

            return {
                nome: { required: true },
            }
        },
        residentesForaDoNucleo() {

            if (!this.nucleo || !this.data.form.residentes) return []

            const membrosIds = this.nucleo.membros.map(membro => membro.id)

            return this.data.form.residentes.filter(residente =>
                membrosIds.indexOf(residente.id) === -1
                && (residente.isResponsavel || membrosIds.indexOf(residente.responsavelCidadaoId) !== -1)
            )
        },
        membroSelecionadoOutroNucleo() {

            if (!this.membroParaAdicionarId) return null

            return this.data.form.nucleosFamiliares.find(nucleo =>
                nucleo.id !== this.nucleo.id
                && nucleo.membros.some(membro => membro.id === this.membroParaAdicionarId)
            ) || null
        },
    },
    created() {

        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        }
    },
    methods: {
        open(nucleo = null) {

            this.editando = !!nucleo
            this.nucleo = nucleo ? { ...nucleo, membros: [ ...nucleo.membros ] } : null
            this.nome = nucleo ? nucleo.nome : ''
            this.membroParaAdicionarId = null
            this.membrosOriginaisIds = nucleo ? nucleo.membros.map(membro => membro.id) : []

            this.$refs.RkmModal.show()
        },
        handleCancel() {

            this.$refs.RkmModal.hide()
        },
        handleSalvar() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.loading = true

                    const salvar = this.editando ? this.salvarEdicao() : this.salvarCriacao()

                    salvar
                        .then(() => this.$refs.RkmModal.hide())
                        .catch(this.handleError)
                        .finally(() => this.loading = false)
                })
        },
        salvarCriacao() {

            const request = {
                domicilioId: this.data.form.domicilioId,
                nome: this.nome,
            }

            return this.repositories.paciente.createNucleoFamiliar(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data }) => this.data.form.nucleosFamiliares.push(data))
        },
        salvarEdicao() {

            const renomear = this.nome !== this.nucleo.nome
                ? this.repositories.paciente.renameNucleoFamiliar(
                    this.nucleo.id,
                    this.camelObjectKeysToSnakeRecursive({ nome: this.nome })
                )
                : Promise.resolve()

            const membrosAtuaisIds = this.nucleo.membros.map(membro => membro.id)

            const adicionar = membrosAtuaisIds.filter(id => this.membrosOriginaisIds.indexOf(id) === -1)
            const remover = this.membrosOriginaisIds.filter(id => membrosAtuaisIds.indexOf(id) === -1)

            return renomear
                .then(() => this.processarSequencialmente(adicionar, cidadaoId =>
                    this.repositories.paciente.addMembroNucleoFamiliar(
                        this.camelObjectKeysToSnakeRecursive({ nucleoFamiliarId: this.nucleo.id, cidadaoId })
                    )
                ))
                .then(() => this.processarSequencialmente(remover, cidadaoId =>
                    this.repositories.paciente.removeNucleoFamiliar(cidadaoId)
                ))
                .then(() => this.atualizarListaNucleos())
        },
        processarSequencialmente(ids, callback) {

            return ids.reduce((promise, id) => promise.then(() => callback(id)), Promise.resolve())
        },
        atualizarListaNucleos() {

            return this.repositories.paciente.getNucleosFamiliares({ domicilio_id: this.data.form.domicilioId })
                .then(({ data }) => {

                    this.data.form.nucleosFamiliares = data

                    const nucleoAtual = data.find(item => item.id === this.nucleo.id)

                    if (nucleoAtual) {

                        this.nucleo = nucleoAtual
                    }
                })
        },
        residenteInfo(cidadaoId) {

            return this.data.form.residentes.find(item => item.id === cidadaoId) || {}
        },
        handleAddMembro() {

            if (this.membroSelecionadoOutroNucleo) {

                const membro = this.residentesForaDoNucleo.find(item => item.id === this.membroParaAdicionarId)

                this.showConfirmation(
                    `${membro ? membro.nome : 'Este paciente'} já pertence ao núcleo
                    "${this.membroSelecionadoOutroNucleo.nome || 'sem identificação'}".
                    Ao salvar, ele será transferido para o núcleo "${this.nome}". Deseja continuar?`,
                    this.addMembro,
                )

                return
            }

            this.addMembro()
        },
        addMembro() {

            const residente = this.data.form.residentes.find(item => item.id === this.membroParaAdicionarId)

            if (!residente) return

            this.adicionarMembroLocal(residente)
            this.membroParaAdicionarId = null
        },
        adicionarMembroLocal(residente) {

            if (this.nucleo.membros.some(membro => membro.id === residente.id)) return

            this.nucleo.membros.push({ id: residente.id, nome: residente.nome })

            if (residente.isResponsavel) {

                this.dependentesDe(residente.id).forEach(dependente => this.adicionarMembroLocal(dependente))
            }
        },
        dependentesDe(cidadaoId) {

            return this.data.form.residentes.filter(item => item.responsavelCidadaoId === cidadaoId)
        },
        removeMembro(membro) {

            this.nucleo.membros = this.nucleo.membros.filter(item => item.id !== membro.id)
        },
    },
}
</script>
